<template>
  <Card class="expense-sheet-header">
    <template #content>
      <div class="header-content">
        <div class="header-field">
          <small class="field-label">{{ t('wkt.client.expensesheets.detail-window.creation_date') }}</small>
          <span class="field-value">{{ formatOnlyDate(data.creationDate) }}</span>
        </div>
        
        <div class="header-field">
          <small class="field-label">{{ t('wkt.client.expensesheets.detail-window.project') }}</small>
          <span class="field-value">{{ data.projectName || '--' }}</span>
        </div>
        
        <div class="header-field">
          <small class="field-label">{{ t('wkt.client.expensesheets.detail-window.status') }}</small>
          <span 
            class="field-value" 
            :style="{ color: getStatusColor(Number(data.status)) }"
            :class="{ 'p-tag-forthcoming': Number(data.status) === -1 }"
          >
            {{ getStatusText(Number(data.status)) }}
          </span>
        </div>
        
        <div class="header-field">
          <small class="field-label">{{ t('wkt.client.expensesheets.detail-window.amount') }}</small>
          <h3 class="field-value amount">{{ formatCurrency(data.amount) }}</h3>
        </div>
      </div>
      </template>
      <template #footer>
        <div class="header-actions">
          <Button
            v-if="[-1, 2].includes(Number(data.status))"
            class="footer-primary-button"
            :label="t('wkt.client.expensesheets.detail-window.button-edit')"
            color="#185EA9"
            variant="text"
            @click="$emit('edit-expense-sheet')"
          />
          <Button
            v-if="[-1, 2].includes(Number(data.status))"
            :label="t('wkt.client.expensesheets.detail-window.button-send')"
            class="footer-button-cancel"
            variant="text"
            :disabled="!hasItems"
            @click="$emit('confirm-send-expense-sheet')"
          />
          <Button
            v-if="[0].includes(Number(data.status))"
            class="footer-primary-button"
            :label="t('wkt.client.expensesheets.detail-window.button-cancel_request')"
            color="#185EA9"
            variant="text"
            :disabled="!isEmployeeCreator"
            @click="$emit('cancel-expense-sheet-request')"
          />
        </div>
      
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDateFormatter } from '@wkt/composables/useDateFormatter'
import { useExpenseSheets } from '@wkt/composables/useExpenseSheets'
import { useUtils } from '@wkt/composables/useUtils'

// Props
interface Props {
  data: any
  employeeId: number | null
  hasItems: boolean
}

const props = defineProps<Props>()

// Emits
defineEmits<{
  'edit-expense-sheet': []
  'confirm-send-expense-sheet': []
  'cancel-expense-sheet-request': []
}>()

// Composables
const { t } = useI18n()
const { formatOnlyDate } = useDateFormatter()
const { getStatusText, getStatusColor } = useExpenseSheets()
const { formatCurrency } = useUtils()

// Computed
const isEmployeeCreator = computed(() => {
  return props.employeeId && props.employeeId === props.data.creatorId
})
</script>

<style scoped>
.expense-sheet-header {
  background-color: var(--light-blue);
  border: 1px solid #B8CFDD;
  border-radius: 8px;
  box-shadow: none;
}

.expense-sheet-header :deep(.p-card-body) {
  padding: 0.5rem;
}

.expense-sheet-header :deep(.p-card-content) {
  padding: 0;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 10px;
  gap: 1.5rem;
}

.header-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.field-label {
  color: #757575;
  font-size: 14px;
}

.field-value {
  color: #000;
  font-size: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.field-value.p-tag-forthcoming {
  color: #424242 !important;
  font-weight: 400;
}

.field-value.amount {
  font-weight: 600;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #B8CFDD;
}
</style>